import { useContext, useEffect, useState } from "react";

import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import { message } from "@src/constants";
import Colors from "@src/constants/Colors";
import LocationContext from "@src/context/location/locationContext";
import ShowErrorToastContext from "@src/context/showErrorToast/showErrorToastContext";

import HistoricalReportContext from "@src/context/HistoricalReports/historicalReportContext";
import EventTypeSelector from "../EventTypeSelecter";
import HRChart from "./HRChart";
import {
  getHistoricalReports,
  getMapHistoricalReportData,
  initialHistoricalReportData,
} from "./helper";
import { EventSelectionBox, StyledModel, StyledRootBox } from "./style";

const HistoricalReportsContainer = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [historicalReportData, setHistoricalReportData] =
    useState<HistoricalReportDataType>(initialHistoricalReportData);

  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;

  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);

  const [{ historicalReportState, historicalReportSetState }] = useContext(
    HistoricalReportContext
  );

  const { tab, filterType, eventOpen } = historicalReportState;

  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;

  const getData = async (filters: HistoricalReportsFilterType) => {
    const res = await getHistoricalReports(filters);
    if (res?.data) {
      setHistoricalReportData(res?.data);
    } else {
      showErrorToastSetState({
        errorMessage: res?.error || "",
        errorTitle: message.ERROR_MESSAGE_DASHBOARD,
        open: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    const filter: HistoricalReportsFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      filterType,
      tab,
      cumulative: "0",
    };
    getData(filter);
  }, [property, store, revenueCenter, date, filterType, tab]);

  const mappedHistoricalReports = getMapHistoricalReportData(
    historicalReportData,
    tab
  );

  return (
    <StyledRootBox isDesktop={isDesktop}>
      <StyledModel
        open={eventOpen}
        onClose={() => {
          historicalReportSetState((pre: HistoricalReportStateType) => {
            return {
              ...pre,
              eventOpen: false,
            };
          });
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <EventSelectionBox
          sx={{
            "&::-webkit-scrollbar": {
              width: 8,
              borderRadius: "5px",
              backgroundColor: Colors.primarygray_200,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 6,
              backgroundColor: Colors.primarygray_400,
            },
          }}
        >
          {eventOpen && (
            <EventTypeSelector
              handleClose={(selectedEvent: string) => {
                historicalReportSetState((pre: HistoricalReportStateType) => {
                  return {
                    ...pre,
                    eventOpen: false,
                    filterType: selectedEvent,
                  };
                });
              }}
              currentEvent={filterType}
            />
          )}
        </EventSelectionBox>
      </StyledModel>
      {Object.values(mappedHistoricalReports)?.map(
        (e: HistoricalReportMappedData) => {
          return (
            <HRChart
              key={e?.title}
              loading={loading}
              title={e?.title || ""}
              firstDataset={e?.firstDataset || []}
              secondDataSet={e?.secondDataSet || []}
              labels={historicalReportData?.labels || []}
              chartType={e?.chartType}
              firstColor={e?.firstColor}
              secondColor={e?.secondColor}
              annotationColor={e?.annotationColor}
              rowData={e}
            />
          );
        }
      )}
    </StyledRootBox>
  );
};

export default HistoricalReportsContainer;
